// File: src/pages/CreateCombinedTarget.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import moment from "moment";
import { useNavigate } from "react-router-dom";

/* === API wrappers (use your paths) === */
import { ProductListView, _PostSave } from "../import/ApiEndPoint/ApiCalling";
import {
  Combined_Target_Save, // POST (JSON or FormData)
  View_CombinedTarget_List, // (kept from earlier; not used here)
} from "../import/ApiEndPoint/Api";

/* ============== CSS once ============== */
const cssOnce = () => {
  if (document.getElementById("ct-inline")) return;
  const s = document.createElement("style");
  s.id = "ct-inline";
  s.innerHTML = `
    .ct-toolbar{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
    .ct-title{font-weight:700;text-transform:uppercase;font-size:18px;margin:0}
    .ct-actions{margin-left:auto;display:flex;gap:8px}
    .ct-table{width:100%;border-collapse:collapse}
    .ct-table th,.ct-table td{border:1px solid #e5e7eb;padding:8px;font-size:12px;vertical-align:middle}
    .ct-sticky-head thead th{position:sticky;top:0;background:#fafafa;z-index:1}
    .ct-num{width:110px}
    .ct-readonly{background:#f9fafb}
    .ct-filterbar{display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap;border:1px solid #e5e7eb;border-radius:12px;padding:8px 10px;margin:8px 0 14px;background:#f8fafc}
    .ct-status{font-size:12px;font-weight:700;color:#0f172a;background:#fff7ed;border:1px solid #fed7aa;padding:8px 10px;border-radius:10px;margin:10px 0;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  `;
  document.head.appendChild(s);
};

/* Sales rate helper */
const getSalesRate = (p) =>
  p?.PriceList?.salesRate ??
  p?.priceList?.salesRate ??
  p?.SalesRate ??
  p?.SaleRate ??
  p?.Product_MRP ??
  0;

/* Robust category/subcategory getters */
const getCategory = (p) =>
  p?.category ??
  p?.Category ??
  p?.Product_Category ??
  p?.CategoryName ??
  p?.cat ??
  "";

const getSubCategory = (p) =>
  p?.SubCategory ??
  p?.subCategory ??
  p?.Subcategory ??
  p?.productSubCategory ??
  p?.SubCategoryName ??
  p?.subcat ??
  "";

const round3 = (n) => Number(Number(n || 0).toFixed(3));

/** Fiscal year end rule: always create targets till March (inclusive)
 *  - If start month is Apr-Dec => end is March of next year
 *  - If start month is Jan-Mar => end is March of same year
 */
const getFiscalEndMonth = (startMoment) => {
  const m = startMoment.month(); // 0..11 (Jan..Dec)
  const y = startMoment.year();
  // April = 3
  if (m >= 3) return moment({ year: y + 1, month: 2, day: 1 }); // March next year
  return moment({ year: y, month: 2, day: 1 }); // March same year
};

const buildMonthListInclusive = (startMoment, endMoment) => {
  const list = [];
  const cur = startMoment.clone().startOf("month");
  const end = endMoment.clone().startOf("month");
  while (cur.isSameOrBefore(end, "month")) {
    list.push(cur.clone());
    cur.add(1, "month");
  }
  return list;
};

export default function CreateCombinedTarget() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [productList, setProductList] = useState([]);
  const [rows, setRows] = useState([]); // [{productId, title, category, subCategory, qty, secondaryQty, secondarySize, price}]
  const [targetMonth, setTargetMonth] = useState(""); // YYYY-MM
  const [incrementPercent, setIncrementPercent] = useState(0); // common box

  // Upload modal
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");

  // Status for auto-create (multi-month)
  const [autoStatus, setAutoStatus] = useState("");
  const [autoStep, setAutoStep] = useState({ i: 0, total: 0 });

  useEffect(() => {
    cssOnce();
  }, []);

  /* ===== Load products & seed rows ===== */
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    ProductListView(u?._id, u?.database)
      .then((res) => {
        const products = Array.isArray(res?.Product) ? res.Product : [];
        const mapped = products.map((p) => {
          const cat = getCategory(p);
          const sub = getSubCategory(p);
          const secondarySize = Number(p?.secondarySize || 0);

          return {
            productId: p?._id || p?.sId || "",
            title:
              p?.Product_Title ||
              p?.Product_Desc ||
              p?.ProductName ||
              p?.name ||
              p?.Title ||
              p?.title ||
              (cat || sub ? `${cat} ${sub}`.trim() : "") ||
              p?._id ||
              p?.sId ||
              "—",
            category: cat || "",
            subCategory: sub || "",
            qty: 0,
            secondaryQty: 0,
            secondarySize,
            price: getSalesRate(p),
          };
        });
        setProductList(products);
        setRows(mapped);
      })
      .catch(() => {
        setProductList([]);
        setRows([]);
      });
  }, []);

  /* ===== Change qty handler (primary Target Qty) ===== */
  const changeQty = (idx, value) =>
    setRows((prev) => {
      const next = prev.slice();
      const row = { ...next[idx] };
      const v = Math.max(Number(value || 0), 0);
      row.qty = v;

      const secSize = Number(row.secondarySize || 0);
      if (secSize > 0) row.secondaryQty = round3(v / secSize);
      else row.secondaryQty = v;

      next[idx] = row;
      return next;
    });

  /* ===== Change secondary qty handler ===== */
  const changeSecondaryQty = (idx, value) =>
    setRows((prev) => {
      const next = prev.slice();
      const row = { ...next[idx] };
      const v = Math.max(Number(value || 0), 0);
      row.secondaryQty = v;

      const secSize = Number(row.secondarySize || 0);
      if (secSize > 0) row.qty = round3(v * secSize);
      else row.qty = v;

      next[idx] = row;
      return next;
    });

  /* ===== Unique categories & subcategories for filters ===== */
  const categories = useMemo(() => {
    const s = new Set();
    productList.forEach((p) => {
      const c = getCategory(p);
      if (c) s.add(String(c));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [productList]);

  const subcategories = useMemo(() => {
    const s = new Set();
    productList.forEach((p) => {
      const c = getCategory(p);
      const sub = getSubCategory(p);
      if (filterCategory) {
        if (String(c) === String(filterCategory) && sub) s.add(String(sub));
      } else {
        if (sub) s.add(String(sub));
      }
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [productList, filterCategory]);

  /* ===== Filtered rows for table ===== */
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const catOk =
        !filterCategory || String(r.category) === String(filterCategory);
      const subOk =
        !filterSubCategory ||
        String(r.subCategory) === String(filterSubCategory);
      return catOk && subOk;
    });
  }, [rows, filterCategory, filterSubCategory]);

  /* ===== Grand total (all rows with qty > 0) ===== */
  const grandTotal = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + (Number(r.qty) || 0) * (Number(r.price) || 0),
        0,
      ),
    [rows],
  );

  /* ===== Template download ===== */
  const handleDownloadTemplate = () => {
    const monthLabel = targetMonth
      ? moment(targetMonth, "YYYY-MM").format("MMMM-YYYY")
      : moment().format("MMMM-YYYY");
    const data = productList.map((p) => ({
      productId: p?._id || p?.sId || "",
      category: getCategory(p) || "",
      subCategory: getSubCategory(p) || "",
      qtyAssign: "",
      price: getSalesRate(p),
      month: monthLabel,
      percentage: "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CombinedTargets");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "combined_target_template.xlsx",
    );
  };

  /* ===== Upload (Excel) - unchanged (still one API call) ===== */
  const handleExcelUpload = async () => {
    if (!uploadFile) {
      swal("Please upload a file");
      return;
    }
    if (!targetMonth) {
      swal("Select the Target Month");
      return;
    }
    setLoading(true);
    setAutoStatus("");
    setAutoStep({ i: 0, total: 0 });

    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("date", moment(targetMonth, "YYYY-MM").format("MMMM-YYYY"));
      fd.append("incrementPercent", String(Number(incrementPercent || 0)));
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      if (u?._id) fd.append("created_by", u._id);
      if (u?.database) fd.append("database", u.database);
      fd.append("userId", "COMBINED");

      await _PostSave(Combined_Target_Save, fd);
      swal("Combined target saved from Excel");
      setModalOpen(false);
    } catch (e) {
      swal("Upload failed", "Please verify the sheet columns", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ===== Build monthly products with compounding increment ===== */
  const buildProductsForMonth = (multiplier) => {
    const products = rows
      .filter((r) => Number(r.qty) > 0)
      .map((r) => {
        const baseQty = Number(r.qty) || 0;
        const price = Number(r.price) || 0;

        const qtyAssign = round3(baseQty * multiplier);

        const secSize = Number(r.secondarySize || 0);
        const secondaryQty =
          secSize > 0 ? round3(qtyAssign / secSize) : qtyAssign;

        const totalPrice = round3(qtyAssign * price);

        return {
          productId: r.productId,
          qtyAssign,
          price,
          totalPrice,
          secondaryQty,
        };
      });

    const gt = products.reduce((s, p) => s + (Number(p.totalPrice) || 0), 0);
    return { products, grandTotal: round3(gt) };
  };

  /* ===== Submit (JSON) + AUTO create till March (fiscal year end) ===== */
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!targetMonth) {
      swal("Select the Target Month");
      return;
    }

    const baseProducts = rows.filter((r) => Number(r.qty) > 0);
    if (!baseProducts.length) {
      swal("Enter quantity for at least one product");
      return;
    }

    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    const start = moment(targetMonth, "YYYY-MM");
    if (!start.isValid()) {
      swal("Invalid Month");
      return;
    }

    const end = getFiscalEndMonth(start);
    const months = buildMonthListInclusive(start, end);

    const inc = Number(incrementPercent || 0);
    const rate = 1 + inc / 100;

    setLoading(true);
    setAutoStep({ i: 0, total: months.length });

    try {
      for (let i = 0; i < months.length; i++) {
        const m = months[i];
        const label = m.format("MMMM-YYYY"); // backend format

        // Compounding each month: month0 = base, month1 = base*(1+inc), month2 = base*(1+inc)^2 ...
        const multiplier = Math.pow(rate, i);

        const { products, grandTotal: monthGrandTotal } =
          buildProductsForMonth(multiplier);

        setAutoStep({ i: i + 1, total: months.length });
        setAutoStatus(`Creating: ${label} (${i + 1}/${months.length})`);

        const payload = {
          date: label,
          incrementPercent: inc,
          products,
          grandTotal: monthGrandTotal,
          // multi-tenant context
          created_by: u?._id,
          database: u?.database,
          userId: "COMBINED",
        };

        await _PostSave(Combined_Target_Save, payload);
      }

      swal(
        "Targets created successfully",
        `Created till ${end.format("MMMM-YYYY")}`,
        "success",
      );
      navigate(-1);
    } catch (e2) {
      // stop immediately if any month fails
      swal(
        "Save failed",
        autoStatus ? `Stopped at: ${autoStatus}` : "Please try again",
        "error",
      );
    } finally {
      setLoading(false);
      setAutoStatus("");
      setAutoStep({ i: 0, total: 0 });
    }
  };

  /* ===== Reset subcategory when category changes ===== */
  useEffect(() => {
    setFilterSubCategory("");
  }, [filterCategory]);

  return (
    <div>
      <Card>
        {/* === Toolbar === */}
        <Row className="m-2 ct-toolbar">
          <h1 className="ct-title">Create Manual Target</h1>
          <div className="ct-actions">
            <Button
              color="secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              color="primary"
              onClick={handleDownloadTemplate}
              disabled={loading}
            >
              Download
            </Button>
            <Button
              color="primary"
              onClick={() => setModalOpen(true)}
              disabled={loading}
            >
              Upload
            </Button>
          </div>
        </Row>

        <CardBody>
          {/* Auto-create status */}
          {loading && (
            <div className="ct-status">
              <Spinner size="sm" />
              <span>{autoStatus || "Working..."}</span>
              {!!autoStep.total && (
                <span style={{ opacity: 0.8 }}>
                  Step {autoStep.i}/{autoStep.total}
                </span>
              )}
            </div>
          )}

          <Form onSubmit={submitHandler}>
            {/* === Top controls === */}
            <Row className="mb-2">
              <Col lg="3" md="4" sm="6" className="mb-1">
                <Label>Target Month</Label>
                <Input
                  type="month"
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                  disabled={loading}
                />
              </Col>

              <Col lg="3" md="4" sm="6" className="mb-1">
                <Label>Increment % (Global)</Label>
                <Input
                  type="number"
                  min="0"
                  value={incrementPercent}
                  onChange={(e) => setIncrementPercent(e.target.value)}
                  disabled={loading}
                />
              </Col>

              <Col lg="3" md="4" sm="6" className="mb-1">
                <Label>Grand Total (Base Month)</Label>
                <Input
                  readOnly
                  className="ct-readonly"
                  value={grandTotal.toFixed(2)}
                />
              </Col>

              <Col lg="3" md="12" sm="12" className="mb-1">
                {/* helpful hint */}
                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 22 }}>
                  Auto creates from selected month till <b>March</b> (financial
                  year end).
                </div>
              </Col>
            </Row>

            {/* === Filters === */}
            <div className="ct-filterbar">
              <div style={{ minWidth: 220 }}>
                <Label>Category</Label>
                <Input
                  type="select"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Input>
              </div>
              <div style={{ minWidth: 220 }}>
                <Label>Subcategory</Label>
                <Input
                  type="select"
                  value={filterSubCategory}
                  onChange={(e) => setFilterSubCategory(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All</option>
                  {subcategories.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Input>
              </div>
            </div>

            {/* === Upload modal === */}
            <Modal
              isOpen={modalOpen}
              toggle={() => (!loading ? setModalOpen(false) : null)}
            >
              <ModalHeader
                toggle={() => (!loading ? setModalOpen(false) : null)}
              >
                Upload Excel File
              </ModalHeader>
              <ModalBody>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  disabled={loading}
                />
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 10 }}>
                  Excel upload submits one sheet for the selected month.
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onClick={handleExcelUpload}
                  disabled={loading}
                >
                  {loading ? "Uploading..." : "Submit"}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => setModalOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>

            {/* === Products table === */}
            <div className="table-responsive">
              <table className="ct-table ct-sticky-head">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>SL NO</th>
                    <th>Category</th>
                    <th>Subcategory</th>
                    <th>Product</th>
                    <th className="ct-num">Price (Sales Rate)</th>
                    <th className="ct-num">Secondary Qty</th>
                    <th className="ct-num">Qty Assign</th>
                    <th className="ct-num">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => {
                    const serial = idx + 1;
                    const total = (Number(r.qty) || 0) * (Number(r.price) || 0);
                    const rowIndex = rows.findIndex(
                      (x) => String(x.productId) === String(r.productId),
                    );
                    return (
                      <tr key={r.productId || `${r.title}-${idx}`}>
                        <td style={{ textAlign: "center", fontWeight: 700 }}>
                          {serial}
                        </td>
                        <td>{r.category || "—"}</td>
                        <td>{r.subCategory || "—"}</td>
                        <td>{r.title || r.productId}</td>
                        <td>
                          <Input
                            readOnly
                            className="ct-readonly"
                            value={Number(r.price).toFixed(2)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={
                              r.secondaryQty !== undefined ? r.secondaryQty : ""
                            }
                            onChange={(e) =>
                              rowIndex > -1 &&
                              changeSecondaryQty(rowIndex, e.target.value)
                            }
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={r.qty}
                            onChange={(e) =>
                              rowIndex > -1 &&
                              changeQty(rowIndex, e.target.value)
                            }
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <Input
                            readOnly
                            className="ct-readonly"
                            value={total.toFixed(2)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ textAlign: "center", padding: 14 }}
                      >
                        No products found for the selected filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Row className="mt-3">
              <Col className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}
